import type { MedplumClient } from '@medplum/core'
import type { Patient } from '@medplum/fhirtypes'
import { ZOCDOC_PATIENT_SYSTEM } from '@/model/middleware-types'

/**
 * Binding a beHuman chart to a Zocdoc patient, by writing Zocdoc's `patient_id` to
 * `Patient.identifier` under `ZOCDOC_PATIENT_SYSTEM`.
 *
 * This is the link the whole integration keys on: it is step 1 of the inbound patient match, so a
 * chart bound here is the chart a future Zocdoc booking resolves to instead of creating a
 * duplicate.
 */

/** Every Zocdoc patient id on a chart — empty when it is not linked. */
export function readZocdocPatientIds(patient: Patient): string[] {
  return (patient.identifier ?? [])
    .filter((identifier) => identifier.system === ZOCDOC_PATIENT_SYSTEM)
    .map((identifier) => identifier.value?.trim())
    .filter((value): value is string => Boolean(value))
}

export type LinkResult =
  | { status: 'linked'; patient: Patient }
  /** Already carried this exact id — nothing was written. */
  | { status: 'already-linked'; patient: Patient }
  /**
   * This Zocdoc patient is already bound to a *different* chart. Never moved; the caller must
   * decide, because moving it would re-point one person's history at another.
   */
  | { status: 'conflict'; existing: string }
  /**
   * This chart already carries a different Zocdoc patient id. Refused rather than added: a chart
   * holds **one**, and a second is a defect — historically ours, because every booking we sent
   * omitted `patient_id` and Zocdoc minted a fresh patient per visit. Call
   * `replaceZocdocPatientId` when the operator means to re-point it.
   */
  | { status: 'holds-other'; existing: string[] }

/**
 * Writes the Zocdoc identifier onto a chart, additively.
 *
 * Four properties worth stating, because each one is a way this could go wrong:
 *
 * - **the chart is re-read immediately before the write**, so the update is built on current
 *   state rather than on whatever the appointments list happened to return minutes ago;
 * - **existing identifiers are preserved.** A chart routinely carries Health Gorilla, DoseSpot and
 *   SSN identifiers; `Patient.identifier` is a list and this appends to it. Replacing the array
 *   would silently unlink the patient from those systems;
 * - **a chart holds exactly one Zocdoc patient id.** A second one is refused (`holds-other`), not
 *   appended: one chart is one Zocdoc patient, and the pile of ids we saw came from our own
 *   booking payload omitting `patient_id` so that Zocdoc created a new patient per visit. That is
 *   fixed at the source, so this enforces the invariant rather than tolerating breaches of it.
 *   Re-pointing is possible but must be asked for — `replaceZocdocPatientId`, one deliberate act
 *   with the old value on screen, never a silent accumulation;
 * - **the one thing outright refused is taking an id off another chart.** If this Zocdoc patient is
 *   already bound elsewhere, moving it would re-point one person's history at another, so it
 *   reports instead. A human still confirms every link; nothing here is inferred.
 *
 * Idempotent: re-linking the same id writes nothing, which is what makes a double-click harmless.
 */
export async function linkZocdocPatient(
  medplum: MedplumClient,
  patientId: string,
  zocdocPatientId: string,
): Promise<LinkResult> {
  const current = await medplum.readResource('Patient', patientId)
  const held = readZocdocPatientIds(current)

  if (held.includes(zocdocPatientId)) return { status: 'already-linked', patient: current }
  // One chart, one Zocdoc patient. The caller decides whether to re-point it.
  if (held.length > 0) return { status: 'holds-other', existing: held }

  // Asked of FHIR rather than of the chart in hand: the question is whether this Zocdoc patient
  // belongs to somebody else, and only a search over every chart can answer it.
  const holder = await medplum.searchOne(
    'Patient',
    `identifier=${ZOCDOC_PATIENT_SYSTEM}|${encodeURIComponent(zocdocPatientId)}`,
  )
  if (holder?.id && holder.id !== patientId) return { status: 'conflict', existing: holder.id }

  const updated = await medplum.updateResource<Patient>({
    ...current,
    identifier: [
      ...(current.identifier ?? []),
      { system: ZOCDOC_PATIENT_SYSTEM, value: zocdocPatientId },
    ],
  })
  return { status: 'linked', patient: updated }
}

/**
 * Re-points a chart at a different Zocdoc patient: every existing Zocdoc identifier is dropped and
 * the given one written in its place.
 *
 * Separate from `linkZocdocPatient` because it is a different decision. Linking answers "is this
 * the same person"; this answers "and the id we recorded before was the wrong one" — which
 * discards a binding somebody previously confirmed, so it exists only where an operator asked for
 * it explicitly and could see what they were replacing.
 *
 * Identifiers from other systems are untouched: a chart carrying Health Gorilla, DoseSpot and SSN
 * ids keeps all of them, exactly as the additive link does.
 *
 * The cross-chart check still applies — an id belonging to another chart is never taken.
 */
export async function replaceZocdocPatientId(
  medplum: MedplumClient,
  patientId: string,
  zocdocPatientId: string,
): Promise<LinkResult> {
  const current = await medplum.readResource('Patient', patientId)

  if (readZocdocPatientIds(current).includes(zocdocPatientId)) {
    const others = (current.identifier ?? []).filter(
      (identifier) => identifier.system !== ZOCDOC_PATIENT_SYSTEM,
    )
    // Already the right id, but possibly beside stale ones — pruning those is the point of the
    // call, so it is not short-circuited as "already linked".
    if (others.length === (current.identifier?.length ?? 0) - 1) {
      return { status: 'already-linked', patient: current }
    }
  }

  const holder = await medplum.searchOne(
    'Patient',
    `identifier=${ZOCDOC_PATIENT_SYSTEM}|${encodeURIComponent(zocdocPatientId)}`,
  )
  if (holder?.id && holder.id !== patientId) return { status: 'conflict', existing: holder.id }

  const updated = await medplum.updateResource<Patient>({
    ...current,
    identifier: [
      ...(current.identifier ?? []).filter(
        (identifier) => identifier.system !== ZOCDOC_PATIENT_SYSTEM,
      ),
      { system: ZOCDOC_PATIENT_SYSTEM, value: zocdocPatientId },
    ],
  })
  return { status: 'linked', patient: updated }
}
